// Investor Hub — submissions shared with the investor (directly or via group),
// submission detail with deck preview + feedback form, and a company-side
// "my submissions" view.
import React, { useState, useEffect } from "react";
import { Ico, StatusPill } from "./primitives.jsx";
import { useResource, apiGet, apiPost, apiPatch, ApiError, navigate } from "./api.jsx";

// ============================================================================
// InvestorHub — list of submissions
// ============================================================================

export function InvestorHub() {
  const [filter, setFilter] = useState("");
  const qs = filter ? `?status=${filter}` : "";
  const list = useResource(`/submissions/${qs}`);
  const groups = useResource("/investor-groups/");
  const rows = list.data?.results || [];

  const openSubmission = (id) => navigate("submission", { submissionId: id });

  return (
    <div className="page page--wide route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Investor hub</div>
          <h1 className="page-title">Decks shared <em>with you</em>.</h1>
          <p className="page-sub" style={{maxWidth:640, marginTop:10}}>
            Submissions routed to you directly or through a group you belong to.
            Open a submission to preview the deck, read the extracted analysis, and leave feedback.
          </p>
        </div>
      </div>

      <div className="row" style={{marginBottom:16, gap:10}}>
        <div className="seg">
          {[
            ["", "All"], ["pending", "Pending"], ["viewed", "Viewed"], ["reviewed", "Reviewed"],
          ].map(([v, l]) => (
            <button key={v} className={filter === v ? "on" : ""} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
        <div className="spacer"/>
        {!groups.loading && groups.data?.results?.length > 0 && (
          <div className="dim" style={{fontSize:13}}>
            Groups: {groups.data.results.map(g => g.name).join(", ")}
          </div>
        )}
      </div>

      <div className="card card--flush">
        <table className="t">
          <thead>
            <tr>
              <th style={{width:"30%"}}>Company</th>
              <th>Submitted via</th>
              <th>Submitted</th>
              <th>Deck</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.loading && <tr><td colSpan={6} style={{padding:30, textAlign:"center", color:"var(--text-mute)"}}>Loading…</td></tr>}
            {!list.loading && rows.length === 0 && (
              <tr><td colSpan={6} style={{padding:40, textAlign:"center", color:"var(--text-mute)"}}>
                No submissions{filter ? ` with status '${filter}'` : ""} yet.
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} style={{cursor:"pointer"}} onClick={() => openSubmission(r.id)}>
                <td>
                  <div style={{fontWeight:500}}>{r.analysis_name}</div>
                  <div className="dim" style={{fontSize:12}}>{r.analysis_type || "—"}</div>
                </td>
                <td className="dim">{r.company_name || "Direct"}</td>
                <td className="dim">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "—"}</td>
                <td className="dim" style={{fontSize:12}}>{r.deck_filename ? "PDF" : "—"}</td>
                <td><SubmissionStatusPill status={r.status}/></td>
                <td className="dim" style={{textAlign:"right"}}>Open →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubmissionStatusPill({ status }) {
  const map = {
    pending: { cls: "pill", label: "Pending" },
    viewed: { cls: "pill pill--accent", label: "Viewed" },
    reviewed: { cls: "pill pill--good", label: "Reviewed" },
  };
  const c = map[status] || { cls: "pill", label: status };
  return <span className={c.cls}>{c.label}</span>;
}

// ============================================================================
// SubmissionDetail — deck preview + feedback form
// ============================================================================

export function SubmissionDetail() {
  const [submissionId, setSubmissionId] = useState(null);
  useEffect(() => {
    try { setSubmissionId(localStorage.getItem("vz-submission-id")); } catch { /* ignore */ }
  }, []);

  const detail = useResource(submissionId ? `/submissions/${submissionId}/` : "", { enabled: !!submissionId });
  const [deckUrl, setDeckUrl] = useState(null);
  const [deckError, setDeckError] = useState(null);

  async function requestDeckUrl(disposition = "inline") {
    try {
      setDeckError(null);
      const resp = await apiGet(`/submissions/${submissionId}/deck-url/?disposition=${disposition}`);
      setDeckUrl(resp.url);
      if (disposition === "attachment") {
        // Open in new tab for download
        window.open(resp.url, "_blank");
      }
    } catch (err) {
      setDeckError(err instanceof ApiError ? (err.body?.error || err.message) : err.message);
    }
  }

  if (!submissionId) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow" style={{marginBottom:10}}>Submission</div>
          <h1 className="page-title">Pick a submission from the hub.</h1>
          <p className="page-sub" style={{marginTop:12}}>No submission selected. Return to the investor hub to choose one.</p>
          <button className="btn btn--primary" style={{marginTop:16}} onClick={() => navigate("investor-hub")}>← Back to hub</button>
        </div>
      </div>
    );
  }

  if (detail.loading) {
    return <div className="page route-enter"><div className="page-header"><div className="h-eyebrow">Loading…</div><h1 className="page-title" style={{opacity:.5}}>Opening submission</h1></div></div>;
  }
  if (detail.error) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow" style={{color:"var(--bad)"}}>Error</div>
          <h1 className="page-title">Couldn't load this submission.</h1>
          <p className="page-sub" style={{marginTop:12}}>{detail.error.message}</p>
          <button className="btn btn--primary" style={{marginTop:16}} onClick={detail.reload}>Retry</button>
        </div>
      </div>
    );
  }

  const s = detail.data;
  const myFeedback = (s.feedback || []).find(f => f.investor_id); // pick any; per-investor endpoint scopes it

  return (
    <div className="page page--wide route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Submission · {s.status}</div>
          <h1 className="page-title">{s.analysis_name}</h1>
          <div className="row" style={{marginTop:12, columnGap:14, rowGap:8, color:"var(--text-mute)", fontSize:14, flexWrap:"wrap"}}>
            <span>{s.analysis_type || "—"}</span>
            <span className="dim">·</span>
            <span>from {s.company_name || "direct submission"}</span>
            <span className="dim">·</span>
            <span className="as-of">{s.submitted_at ? new Date(s.submitted_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"}) : "—"}</span>
          </div>
          {s.submission_message && (
            <p className="page-sub" style={{marginTop:16, maxWidth:720, fontStyle:"italic"}}>
              "{s.submission_message}"
            </p>
          )}
        </div>
        <div className="row" style={{gap:10}}>
          <button className="btn btn--ghost" onClick={() => navigate("investor-hub")}>← Back</button>
          <button className="btn btn--ghost" onClick={() => requestDeckUrl("attachment")} disabled={!s.deck_filename}>Download deck</button>
          <button
            className="btn btn--ghost"
            onClick={() => {
              navigate("results", { analysisId: s.analysis_id });
            }}
          >
            View full analysis →
          </button>
        </div>
      </div>

      <div className="grid" style={{gridTemplateColumns:"1.3fr 1fr", gap:24, alignItems:"start"}}>
        {/* Deck preview */}
        <div className="card" style={{padding:0, minHeight:560, overflow:"hidden"}}>
          {!deckUrl ? (
            <div style={{padding:"60px 20px", textAlign:"center"}}>
              <Ico n="upload" size={36}/>
              <h3 className="h-display" style={{fontSize:"var(--text-2xl)", marginTop:16}}>
                {s.deck_filename || "No deck attached"}
              </h3>
              {s.deck_filename && (
                <button className="btn btn--primary" style={{marginTop:20}} onClick={() => requestDeckUrl("inline")}>
                  Preview deck inline
                </button>
              )}
              {deckError && <p style={{color:"var(--bad)", marginTop:12, fontSize:13}}>{deckError}</p>}
            </div>
          ) : (
            <iframe
              src={deckUrl}
              title="Deck preview"
              style={{width:"100%", height:"72vh", border:"none"}}
            />
          )}
        </div>

        {/* Feedback form */}
        <FeedbackPanel submissionId={submissionId} existing={myFeedback}/>
      </div>
    </div>
  );
}

function FeedbackPanel({ submissionId, existing }) {
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    pre_screen_pass: existing?.pre_screen_pass || "",
    overall_score: existing?.overall_score || "",
    investment_decision: existing?.investment_decision || "",
    strengths: existing?.strengths || "",
    weaknesses: existing?.weaknesses || "",
    questions: existing?.questions || "",
    additional_comments: existing?.additional_comments || "",
  });

  const set = (k) => (e) => setForm({...form, [k]: e.target.value});

  async function save() {
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const body = {...form};
      // numbers
      if (body.overall_score !== "") body.overall_score = Number(body.overall_score);
      await apiPost(`/submissions/${submissionId}/feedback/`, body);
      setNotice("Feedback saved.");
    } catch (err) {
      setError(err instanceof ApiError ? (err.body?.error || err.message) : err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card--flush">
      <div className="card-header">
        <div>
          <div className="eyebrow">Your feedback</div>
          <div className="title">Leave your read</div>
        </div>
      </div>
      <div className="card-body" style={{display:"flex", flexDirection:"column", gap:14}}>
        {notice && <div style={{padding:"10px 12px", border:"1px solid var(--good)", borderRadius:4, background:"color-mix(in srgb, var(--good) 10%, transparent)", fontSize:13}}>{notice}</div>}
        {error && <div style={{padding:"10px 12px", border:"1px solid var(--bad)", borderRadius:4, background:"color-mix(in srgb, var(--bad) 10%, transparent)", fontSize:13, color:"var(--bad)"}}>{error}</div>}

        <FormField label="Pre-screen" value={form.pre_screen_pass} onChange={set("pre_screen_pass")} select options={[
          {v:"", l:"—"}, {v:"pass", l:"Pass"}, {v:"fail", l:"Fail"}, {v:"undecided", l:"Undecided"},
        ]}/>

        <FormField label="Decision" value={form.investment_decision} onChange={set("investment_decision")} select options={[
          {v:"", l:"—"}, {v:"pass", l:"Pass"}, {v:"maybe", l:"Maybe"}, {v:"interested", l:"Interested"}, {v:"very_interested", l:"Very interested"},
        ]}/>

        <FormField label="Overall score (1–10)" type="number" value={form.overall_score} onChange={set("overall_score")}/>

        <FormField label="Strengths" textarea value={form.strengths} onChange={set("strengths")} placeholder="What stands out, specifically?"/>
        <FormField label="Weaknesses" textarea value={form.weaknesses} onChange={set("weaknesses")} placeholder="What worries you?"/>
        <FormField label="Questions for the founder" textarea value={form.questions} onChange={set("questions")} placeholder="One per line."/>

        <button className="btn btn--primary" onClick={save} disabled={saving} style={{marginTop:8}}>
          {saving ? "Saving…" : "Save feedback"}
        </button>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", textarea, select, options, placeholder }) {
  const common = {
    value: value ?? "",
    onChange,
    placeholder,
    style: {
      width:"100%", boxSizing:"border-box",
      border:"1px solid var(--line-strong)", borderRadius:4,
      padding:"10px 12px", background:"var(--surface)",
      fontSize:14, color:"var(--text)", outline:"none",
    },
  };
  return (
    <label style={{display:"block"}}>
      <div className="h-eyebrow" style={{marginBottom:6}}>{label}</div>
      {textarea ? (
        <textarea {...common} rows={3} style={{...common.style, minHeight:80, resize:"vertical", fontFamily:"inherit"}}/>
      ) : select ? (
        <select {...common}>
          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input {...common} type={type}/>
      )}
    </label>
  );
}

// ============================================================================
// MySubmissions — company-side view (decks they've submitted)
// ============================================================================

export function MySubmissions() {
  const list = useResource("/submissions/");
  const rows = list.data?.results || [];

  return (
    <div className="page page--wide route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>My submissions</div>
          <h1 className="page-title">Where your <em>decks</em> landed.</h1>
          <p className="page-sub" style={{maxWidth:640, marginTop:10}}>
            Every submission you've sent — along with which investors have opened, reviewed, or provided feedback.
          </p>
        </div>
        <div className="row" style={{gap:10}}>
          <button className="btn btn--primary" onClick={() => navigate("analyzer")}>
            <Ico n="upload" size={14}/> New analysis
          </button>
        </div>
      </div>

      <div className="card card--flush">
        <table className="t">
          <thead>
            <tr>
              <th style={{width:"30%"}}>Analysis</th>
              <th>Sent to</th>
              <th>Submitted</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.loading && <tr><td colSpan={4} style={{padding:30, textAlign:"center", color:"var(--text-mute)"}}>Loading…</td></tr>}
            {!list.loading && rows.length === 0 && (
              <tr><td colSpan={4} style={{padding:40, textAlign:"center", color:"var(--text-mute)"}}>
                No submissions yet. <button className="btn btn--primary" style={{marginLeft:12}} onClick={() => navigate("analyzer")}>Upload a deck</button>
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                <td>
                  <div style={{fontWeight:500}}>{r.analysis_name}</div>
                  <div className="dim" style={{fontSize:12}}>{r.analysis_type || "—"}</div>
                </td>
                <td className="dim">{r.investor_email || r.investor_group_name || "—"}</td>
                <td className="dim">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "—"}</td>
                <td><SubmissionStatusPill status={r.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
